import { LocationSyncFromCsvResponse } from "@/web-sockets/socket.types";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogProps,
  Typography,
  Checkbox,
  FormControlLabel,
} from "@mui/material";
import { grey } from "@mui/material/colors";
import { FC, useState } from "react";
import { AcceptChangesFromCsvDto } from "service/api/estabrookSchemas";
import { useAdminLocationControllerAcceptUpdatesFromCsv } from "service";
import { toast } from "react-toastify";
import useCsvUploadStore from "../../state/csv-upload-store";

export interface CsvImportModalProps
  extends Omit<
    DialogProps,
    "onClose" | "open" | "style" | "inputValue" | "handler"
  > {
  onClose: () => void;
  open?: boolean;
  status?: CsvImportStatus;
  reviewData?: LocationSyncFromCsvResponse;
}

export type CsvImportStatus = "success" | "updating";

const CsvImportModal: FC<CsvImportModalProps> = (props) => {
  const { sx, onClose, open, status, reviewData, ...rest } = props;

  const {
    cancelUpload,
    setAcceptingFromMainPage,
    setApplyingChanges,
    closeModal,
  } = useCsvUploadStore();

  const [selections, setSelections] = useState<AcceptChangesFromCsvDto>({
    newAddressAndCustomer: true,
    existingAddressNewCustomer: true,
    updateCustomerOnExistingAddress: true,
    updateAddressOfExistingCustomer: true,
    existingCustomerNewAddress: true,
  });

  // Mutation to accept CSV updates
  const { mutate: acceptCsvUpdates, isLoading: isAccepting } =
    useAdminLocationControllerAcceptUpdatesFromCsv({
      onSuccess: () => {
        // Close modal immediately
        closeModal();
        onClose();

        // Set applying changes state to show in sidebar tooltip
        setApplyingChanges("Applying your changes");

        // Set flag to indicate this was an update operation
        useCsvUploadStore.setState((state) => ({
          ...state,
          wasUpdate: true,
        }));
        // Wait for WebSocket completion event to trigger success callback
      },
      onError: (error) => {
        console.error("❌ CSV Modal: CSV accept failed:", error);
        setAcceptingFromMainPage(false);
        toast.error("Failed to apply CSV updates. Please try again.");
      },
    });

  // Mutation to cancel CSV upload by rejecting all changes
  const { mutate: cancelCsvUpload, isLoading: isCancelling } =
    useAdminLocationControllerAcceptUpdatesFromCsv({
      onSuccess: () => {
        toast.success("Upload cancelled successfully");
        // State already reset in handleCancel for immediate feedback
        onClose(); // Close modal after successful cancellation
      },
      onError: (error) => {
        console.error("CSV cancel failed:", error);
        toast.error("Failed to cancel upload. Please try again.");
        // If API fails, we might want to restore the previous state
        // For now, we'll leave it cancelled since user intended to cancel
      },
    });

  // Update selections when reviewData changes to set false for fields with 0 count
  const getEffectiveSelections = (): AcceptChangesFromCsvDto => {
    if (!reviewData) return selections;

    return {
      newAddressAndCustomer:
        reviewData.newAddressAndCustomer > 0
          ? selections.newAddressAndCustomer
          : false,
      existingAddressNewCustomer:
        reviewData.existingAddressNewCustomer > 0
          ? selections.existingAddressNewCustomer
          : false,
      updateCustomerOnExistingAddress:
        reviewData.updateCustomerOnExistingAddress > 0
          ? selections.updateCustomerOnExistingAddress
          : false,
      updateAddressOfExistingCustomer:
        reviewData.updateAddressOfExistingCustomer > 0
          ? selections.updateAddressOfExistingCustomer
          : false,
      existingCustomerNewAddress:
        reviewData.existingCustomerNewAddress > 0
          ? selections.existingCustomerNewAddress
          : false,
    };
  };

  const handleSelectionChange = (
    key: keyof AcceptChangesFromCsvDto,
    checked: boolean,
  ) => {
    setSelections((prev) => ({
      ...prev,
      [key]: checked,
    }));
  };

  // Check if any changes are selected
  const hasSelectedChanges = (): boolean => {
    const effectiveSelections = getEffectiveSelections();
    return Object.values(effectiveSelections).some(Boolean);
  };

  // Check if any operation is in progress
  const isOperationInProgress = (): boolean => {
    return status === "updating" || isAccepting || isCancelling;
  };

  const handleUpdateCustomers = () => {
    // Call the API endpoint directly with the selected changes
    acceptCsvUpdates({
      body: getEffectiveSelections(),
    });
  };

  const handleCancel = () => {
    // Immediately reset state for better UX
    cancelUpload();

    // Cancel the upload by rejecting all changes
    cancelCsvUpload({
      body: {
        newAddressAndCustomer: false,
        existingAddressNewCustomer: false,
        updateCustomerOnExistingAddress: false,
        updateAddressOfExistingCustomer: false,
        existingCustomerNewAddress: false,
      },
    });
  };

  const getContent = () => {
    if (status === "updating") {
      return (
        <>
          <CircularProgress />
          <Box
            sx={{
              marginTop: "40px",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: "16px",
              color: grey[100],
              textAlign: "center",
            }}
          >
            <Typography variant="titleLarge">Updating Customer Data</Typography>
            <Typography variant="bodyMedium">
              Applying the selected changes to the database...
            </Typography>
          </Box>
        </>
      );
    }

    if (status === "success" && reviewData) {
      return (
        <>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "flex-start",
              gap: "24px",
              color: grey[100],
              width: "100%",
            }}
          >
            <Box sx={{ textAlign: "center", width: "100%" }}>
              <Typography variant="titleLarge">
                Review Changes Before Import
              </Typography>
            </Box>

            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: "16px",
                width: "100%",
              }}
            >
              {/* Show validation errors as disabled form control labels */}
              {reviewData.validationError > 0 &&
                reviewData.validationErrors.map((validationError, index) => (
                  <FormControlLabel
                    key={`validation-error-${index}`}
                    disabled
                    control={<Checkbox disabled checked={false} />}
                    label={
                      <Typography
                        variant="bodyMedium"
                        sx={{
                          color: grey[500],
                        }}
                      >
                        Row {validationError.row}:{" "}
                        {validationError.errors.join(", ")}
                      </Typography>
                    }
                  />
                ))}

              {reviewData.newAddressAndCustomer > 0 && (
                <FormControlLabel
                  disabled={reviewData.validationError > 0}
                  control={
                    <Checkbox
                      disabled={reviewData.validationError > 0}
                      checked={
                        reviewData.validationError > 0
                          ? false
                          : selections.newAddressAndCustomer
                      }
                      onChange={(e) =>
                        handleSelectionChange(
                          "newAddressAndCustomer",
                          e.target.checked,
                        )
                      }
                    />
                  }
                  label={
                    <Typography
                      variant="bodyMedium"
                      sx={{
                        color:
                          reviewData.validationError > 0
                            ? grey[500]
                            : "inherit",
                      }}
                    >
                      Add and invite {reviewData.newAddressAndCustomer} new
                      customers
                    </Typography>
                  }
                />
              )}

              {reviewData.existingAddressNewCustomer > 0 && (
                <FormControlLabel
                  disabled={reviewData.validationError > 0}
                  control={
                    <Checkbox
                      disabled={reviewData.validationError > 0}
                      checked={
                        reviewData.validationError > 0
                          ? false
                          : selections.existingAddressNewCustomer
                      }
                      onChange={(e) =>
                        handleSelectionChange(
                          "existingAddressNewCustomer",
                          e.target.checked,
                        )
                      }
                    />
                  }
                  label={
                    <Typography
                      variant="bodyMedium"
                      sx={{
                        color:
                          reviewData.validationError > 0
                            ? grey[500]
                            : "inherit",
                      }}
                    >
                      {reviewData.existingAddressNewCustomer} customers will
                      receive invites due to email changes on existing addresses
                    </Typography>
                  }
                />
              )}

              {reviewData.updateAddressOfExistingCustomer > 0 && (
                <FormControlLabel
                  disabled={reviewData.validationError > 0}
                  control={
                    <Checkbox
                      disabled={reviewData.validationError > 0}
                      checked={
                        reviewData.validationError > 0
                          ? false
                          : selections.updateAddressOfExistingCustomer
                      }
                      onChange={(e) =>
                        handleSelectionChange(
                          "updateAddressOfExistingCustomer",
                          e.target.checked,
                        )
                      }
                    />
                  }
                  label={
                    <Typography
                      variant="bodyMedium"
                      sx={{
                        color:
                          reviewData.validationError > 0
                            ? grey[500]
                            : "inherit",
                      }}
                    >
                      Update {reviewData.updateAddressOfExistingCustomer}{" "}
                      addresses data
                    </Typography>
                  }
                />
              )}

              {reviewData.updateCustomerOnExistingAddress > 0 && (
                <FormControlLabel
                  disabled={reviewData.validationError > 0}
                  control={
                    <Checkbox
                      disabled={reviewData.validationError > 0}
                      checked={
                        reviewData.validationError > 0
                          ? false
                          : selections.updateCustomerOnExistingAddress
                      }
                      onChange={(e) =>
                        handleSelectionChange(
                          "updateCustomerOnExistingAddress",
                          e.target.checked,
                        )
                      }
                    />
                  }
                  label={
                    <Typography
                      variant="bodyMedium"
                      sx={{
                        color:
                          reviewData.validationError > 0
                            ? grey[500]
                            : "inherit",
                      }}
                    >
                      {reviewData.updateCustomerOnExistingAddress} customers
                      data will be updated
                    </Typography>
                  }
                />
              )}

              {reviewData.existingCustomerNewAddress > 0 && (
                <FormControlLabel
                  disabled={reviewData.validationError > 0}
                  control={
                    <Checkbox
                      disabled={reviewData.validationError > 0}
                      checked={
                        reviewData.validationError > 0
                          ? false
                          : selections.existingCustomerNewAddress
                      }
                      onChange={(e) =>
                        handleSelectionChange(
                          "existingCustomerNewAddress",
                          e.target.checked,
                        )
                      }
                    />
                  }
                  label={
                    <Typography
                      variant="bodyMedium"
                      sx={{
                        color:
                          reviewData.validationError > 0
                            ? grey[500]
                            : "inherit",
                      }}
                    >
                      {reviewData.existingCustomerNewAddress} New Addresses
                      added
                    </Typography>
                  }
                />
              )}
            </Box>

            <Box
              sx={{
                display: "flex",
                gap: "16px",
                width: "100%",
                justifyContent: "flex-end",
              }}
            >
              <Button
                variant="outlined"
                onClick={handleCancel}
                disabled={isOperationInProgress()}
              >
                {isCancelling ? "Cancelling..." : "Cancel Upload"}
              </Button>
              <Button
                variant="contained"
                onClick={handleUpdateCustomers}
                disabled={
                  isOperationInProgress() ||
                  !hasSelectedChanges() ||
                  reviewData.validationError > 0
                }
              >
                {isAccepting
                  ? "Updating..."
                  : reviewData.validationError > 0
                    ? "Cannot Import - Has Validation Errors"
                    : hasSelectedChanges()
                      ? "Update Customers"
                      : "No Changes Selected"}
              </Button>
            </Box>
          </Box>
        </>
      );
    }

    return null;
  };

  if (!status) return null;

  return (
    <Dialog
      onClose={() => {
        // Prevent closing while updating or cancelling
        if (isOperationInProgress()) return;
        onClose();
      }}
      disableEscapeKeyDown={isOperationInProgress()}
      {...rest}
      sx={sx || {}}
      open={open || false}
    >
      <DialogContent
        sx={{
          padding: "56px",
          display: "flex",
          flexDirection: "column",
          gap: "40px",
          alignItems: "center",
          minWidth: "500px",
        }}
      >
        {getContent()}
      </DialogContent>
    </Dialog>
  );
};

export default CsvImportModal;
